// src/components/NewCarCard.tsx

import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  useMediaQuery,
  useTheme,
  Grid,
  Stack,
  Divider,
  Chip,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { encodeId } from '../utils/idEncoder';

type NewCarCardProps = {
  vehicle?: any;
  viewMode?: 'grid' | 'list';
};

const NewCarCard = ({ vehicle, viewMode = 'grid' }: NewCarCardProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const effectiveViewMode = isMobile ? 'grid' : viewMode;
  const isList = effectiveViewMode === 'list';

  if (!vehicle) return null;

  const formatPrice = (price: number | null) => {
    if (price === null || price === undefined) return '';
    return new Intl.NumberFormat('en-SG', {
      style: 'currency',
      currency: 'SGD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatName = (brand?: string, model?: string): string => {
    const full = [brand, model].filter(Boolean).join(' ');
    if (!full) return "Unknown Vehicle";
    return full.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const variants = vehicle.variants || [];
  const displayVariants = isList ? variants.slice(0, 3) : []; // Show up to 3 variants in list view

  // ✅ NEW: Determine if the main vehicle price should show "Ask for price"
  const isAskForMainPrice = vehicle.askForPrice || !vehicle.startingPrice || vehicle.startingPrice === 0;

  return (
    <Card
      component={RouterLink}
      to={`/new-vehicle-details/${encodeId(vehicle.newVehicleId)}`}
      sx={{
        display: 'flex',
        flexDirection: isList ? 'row' : 'column',
        width: '100%',
        minHeight: isList ? 220 : 'auto',
        height: isList ? 'auto' : '100%',
        borderRadius: '16px',
        border: '1px solid rgba(0,0,0,0.04)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        position: 'relative',
        textDecoration: 'none',
        overflow: 'hidden',
        color: 'black',
        transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
        bgcolor: '#fff',
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: '0 12px 30px rgba(0, 0, 0, 0.12)',
          '& .vehicle-img': { transform: 'scale(1.05)' },
        },
      }}
    >
      {/* Promo Offer Tag */}
      {vehicle.promoOffer && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            bgcolor: '#FFA100', // Matched to dealer name color
            color: '#fff',
            fontWeight: 800,
            fontSize: '0.65rem',
            px: 1.2,
            py: 0.5,
            borderRadius: '6px',
            boxShadow: '0 2px 8px rgba(255, 161, 0, 0.4)', // Matched shadow color
            zIndex: 2,
            textTransform: 'uppercase',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }}
        >
          <LocalOfferIcon sx={{ fontSize: 12 }} />
          Promo
        </Box>
      )}

      {/* --- IMAGE SECTION --- */}
      <Box
        sx={{
          position: 'relative',
          width: isList ? 280 : '100%',
          minWidth: isList ? 280 : 'auto',
          height: isList ? 'auto' : 'auto',
          minHeight: isList ? 200 : 'auto',
          aspectRatio: isList ? 'auto' : '4 / 3',
          backgroundColor: '#f8f9fa',
          flexShrink: 0,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRight: isList ? '1px solid #eee' : 'none',
        }}
      >
        <CardMedia
          component="img"
          className="vehicle-img"
          image={vehicle.imageUrl || 'https://via.placeholder.com/600x400?text=No+Image'}
          alt={vehicle.modelName || 'Vehicle'}
          loading="lazy"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover', 
            objectPosition: 'center',
            transition: 'transform 0.5s ease',
          }}
        />
        
        {/* Variant Count Badge */}
        {vehicle.variantCount > 1 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.7)',
              color: '#fff',
              px: 1.2,
              py: 0.4,
              borderRadius: '6px',
              fontSize: '0.7rem',
              fontWeight: 700,
              backdropFilter: 'blur(4px)'
            }}
          >
            {vehicle.variantCount} Variants
          </Box>
        )}
      </Box>

      {/* --- CONTENT SECTION --- */}
      <CardContent
        sx={{
          flex: 1,
          p: isList ? 2.5 : 2,
          display: 'flex',
          flexDirection: 'column',
          '&:last-child': { pb: isList ? 2.5 : 2 },
          minWidth: 0,
        }}
      >
        <Stack spacing={1} sx={{ height: '100%' }}>
          {/* ================= GRID MODE ================= */}
          {!isList && (
            <>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {isAskForMainPrice ? 'Pricing' : 'From'}
              </Typography>
              
              {/* ✅ NEW: Beautiful Ask For Price Indicator (Grid Mode) */}
              {isAskForMainPrice ? (
                <Typography variant="subtitle1" fontWeight={800} color="primary.main" sx={{ mt: -0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Ask us for price
                </Typography>
              ) : (
                <Typography variant="h5" fontWeight={800} color="#1a2027" sx={{ mt: -0.5 }}>
                  {formatPrice(vehicle.startingPrice)}
                </Typography>
              )}

              <Typography
                variant="subtitle1"
                fontWeight={700}
                color="text.primary"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineHeight: 1.3,
                  mt: 0.5
                }}
              >
                {formatName(vehicle.brandName, vehicle.modelName)}
              </Typography>

              <Typography variant="body2" color="text.secondary" noWrap sx={{ fontStyle: 'italic', mb: 1 }}>
                {vehicle.shortSummary || 'Authorized Dealer'}
              </Typography>

              <Divider sx={{ borderColor: '#f0f0f0', mt: 'auto', mb: 1 }} />

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="caption" fontWeight={700} color="#FFA100" noWrap>
                  {vehicle.dealerName}
                </Typography>
              </Box>
            </>
          )}

          {/* ================= LIST MODE ================= */}
          {isList && (
            <>
              <Grid container justifyContent="space-between" alignItems="flex-start" wrap="nowrap">
                <Grid size={{ xs: 8 }} sx={{ minWidth: 0 }}>
                  <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.2, mb: 0.5 }}>
                    {formatName(vehicle.brandName, vehicle.modelName)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    {vehicle.shortSummary}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 4 }} textAlign="right" sx={{ flexShrink: 0 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    {isAskForMainPrice ? 'Pricing' : 'Starting from'}
                  </Typography>

                  {/* ✅ NEW: Beautiful Ask For Price Indicator (List Mode Top Right) */}
                  {isAskForMainPrice ? (
                    <Typography variant="subtitle1" fontWeight={800} color="primary.main" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Ask us for price
                    </Typography>
                  ) : (
                    <Typography variant="h5" fontWeight={800} color="#1a2027">
                      {formatPrice(vehicle.startingPrice)}
                    </Typography>
                  )}
                </Grid>
              </Grid>

              <Divider sx={{ my: 1.5 }} />

              {/* Variants Preview for List Mode */}
              {displayVariants.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', mb: 1, display: 'block', letterSpacing: '0.5px' }}>
                    Available Trims
                  </Typography>
                  <Stack spacing={0.5}>
                    {displayVariants.map((v: any) => {
                      const specsList = [v.fuelConsumption, v.maximumPowerOutput, v.transmissionType]
                        .filter(item => item && item.trim() !== '')
                        .join(' • ');

                      // ✅ NEW: Determine if this specific variant should show "Ask for price"
                      const isVariantAskForPrice = !v.currentPrice || v.currentPrice === 0;

                      return (
                        <Box
                          key={v.vehicleVariantId}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            bgcolor: '#f8f9fa',
                            py: 1,
                            px: 1.5,
                            borderRadius: '8px',
                            transition: 'background-color 0.2s',
                            '&:hover': { bgcolor: '#f1f3f5' }
                          }}
                        >
                          {/* Variant Name */}
                          <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ width: '35%', pr: 1 }} noWrap>
                            {v.variantName}
                          </Typography>

                          {/* ✅ NEW: Styled Chip for Variant Ask Price */}
                          <Box sx={{ width: '25%' }}>
                            {isVariantAskForPrice ? (
                              <Chip 
                                label="Ask for price" 
                                size="small" 
                                sx={{ 
                                  bgcolor: '#eef2ff', 
                                  color: '#4f46e5', // A nice vibrant indigo
                                  fontWeight: 700, 
                                  fontSize: '0.65rem', 
                                  height: 22,
                                  borderRadius: 1
                                }} 
                              />
                            ) : (
                              <Typography variant="subtitle2" fontWeight={800} color="#1a2027">
                                {formatPrice(v.currentPrice)}
                              </Typography>
                            )}
                          </Box>

                          {/* Specifications */}
                          <Typography variant="caption" color="text.secondary" sx={{ width: '40%', textAlign: 'right' }} noWrap>
                            {specsList || '-'}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Stack>
                  {vehicle.variantCount > 3 && (
                    <Typography 
                      variant="caption" 
                      color="primary.main" 
                      fontWeight={600} 
                      sx={{ 
                        mt: 1, 
                        display: 'inline-block', 
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      View all {vehicle.variantCount} variants
                    </Typography>
                  )}
                </Box>
              )}

              <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" fontWeight={700} color="#FFA100">
                  {vehicle.dealerName}
                </Typography>
                {vehicle.promoOffer && (
                  <Chip 
                    label={`Promo: ${vehicle.promoOffer}`} 
                    size="small" 
                    variant="outlined" 
                    sx={{ 
                      fontWeight: 600, 
                      color: '#FFA100', 
                      borderColor: '#FFA100' 
                    }} 
                  />
                )}
              </Box>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default NewCarCard;